import { auth } from "../firebaseConfig";
import store from "../store";
import { companyActions } from "../store/company-slice";
import { fetchCompany } from "../utils/http/companies";
import { queryClient } from "../utils/http/queryClient";
import { sleep } from "../utils/system";

// Switches the active company on the fly to the customer identified by `requested`
// (a company slug/name or id). Used to support a `?customer=<name|id>` query param
// on any page (e.g. `/mail-templates/nl/...?customer=vanbeckevoort`).
//
// Access control is enforced by fetchCompany: the backend returns 403/404 when
// the user is not allowed to see the requested customer. In that case we keep
// the currently active company instead of switching.
export async function switchToCustomer(requested: string | null | undefined): Promise<void> {
  if (!requested) return;

  // Already on the requested customer (matched by slug/name or id) — nothing to do.
  const active = store.getState().company.activeCompany;
  if (active && (active.name === requested || active.id === requested)) return;

  // On hard refreshes Firebase may still be initialising; give it a moment so a
  // token is available for the access check (same pattern as rootLoader).
  for (let i = 0; i < 10; i++) {
    if (await auth?.currentUser?.getIdToken()) break;
    await sleep(100);
  }

  try {
    const company = await queryClient.fetchQuery({
      queryKey: ["company", requested],
      queryFn: ({ signal }) => fetchCompany({ companyNameOrId: requested, signal }),
      staleTime: 2000,
    });
    store.dispatch(companyActions.setActiveCompany(company));
  } catch (error) {
    // No access to (or unknown) customer — leave the current company untouched.
    console.warn(`Could not switch to customer "${requested}":`, error);
  }
}

// Loader-side convenience: reads the `customer` query param off the request URL
// and switches to it. Awaited by company-scoped loaders so that data fetched in
// the loader already targets the requested customer on a hard page load.
export async function applyCustomerFromQuery(request: Request): Promise<void> {
  await switchToCustomer(new URL(request.url).searchParams.get("customer"));
}
